import { Component, OnInit, ViewChild, AfterViewInit, ElementRef } from '@angular/core';
import { Location } from '@angular/common';
import { Table } from 'primeng/table';
import { BreadcrumbService } from '../../app.breadcrumb.service';

import { VenteService } from '../../services/vente-service';
import { VenteEntite } from '../../entites/vente';
import { MenuItem } from 'primeng/api/menuitem';
import { MessageService, LazyLoadEvent, ConfirmationService, SortMeta } from 'primeng/api';
import { ToastModule } from 'primeng/toast';

import { DialogService } from 'primeng/dynamicdialog';
import { Router, ActivatedRoute } from '@angular/router';
import { BaseComponent } from 'src/app/base/base-component';
import { DonneesBaseService } from 'src/app/services/donnees-base-service';
import { ProfilService } from 'src/app/services/profil-service';
import { ContextMenu } from 'primeng/contextmenu/public_api';
import { EStatuts } from 'src/app/entites/enums/statuts';
import { AppMainComponent } from 'src/app/app.main.component';
import { Statut } from 'src/app/entites/statut';
import { Subscription } from 'rxjs';

/**
 * @component VentesComponent
 * @description Component for managing and displaying sales (ventes) in a table format.
 * Provides functionality for listing, filtering, sorting, and managing sales records.
 */
@Component({
    templateUrl: './ventes.component.html',
    styleUrls: ['./ventes.component.scss'],
    styles: [`
        @media screen and (max-width: 960px) {
            :host ::ng-deep .p-datatable.p-datatable-customers.rowexpand-table .p-datatable-tbody > tr > td:nth-child(6) {
                display: flex;
            }
        }
    `],
    providers: [MessageService, ConfirmationService, DialogService],
    host: {
        '(document:keydown)': 'handleKeyboardEvents($event)'
    }
})
export class VentesComponent extends BaseComponent implements OnInit, AfterViewInit {

    ventes: VenteEntite[];

    detailVenteId: number;
    afficherDetailVente: boolean = false;

    statuts: Statut[] = this.sdb.Statuts();

    chargementVentesComplete: boolean = false;
    totalRecords: number = 0;
    lastRequest: Subscription;

    tableParams = this.createTableFilter();
    globalFilter: string = '';
    sortMeta: SortMeta[] = [
        { field: 'dateCreation', order: -1 },
        { field: 'venteId', order: -1 }
    ];

    menuContextuel: MenuItem[];

    // Column configuration for table display
    colonnesDisponibles = [
        { champ: 'numero', entete: 'Numéro', afficheParDefaut: true },
        { champ: 'description', entete: 'Description', afficheParDefaut: true },
        { champ: 'client', entete: 'Client', afficheParDefaut: true },
        { champ: 'employe', entete: 'Employé responsable', afficheParDefaut: true },
        { champ: 'dateLivraison', entete: 'Date de livraison', afficheParDefaut: true },
        { champ: 'dateCreation', entete: 'Date de création', afficheParDefaut: false },
        { champ: 'creePar', entete: 'Créé par', afficheParDefaut: false }
    ];

    private _selectedColumns = [
        { champ: 'numero', entete: 'Numéro', afficheParDefaut: true },
        { champ: 'description', entete: 'Description', afficheParDefaut: true },
        { champ: 'client', entete: 'Client', afficheParDefaut: true },
        { champ: 'employe', entete: 'Employé responsable', afficheParDefaut: true },
        { champ: 'dateLivraison', entete: 'Date de livraison', afficheParDefaut: true }
    ];

    get selectedColumns() {
        return this._selectedColumns;
    }

    set selectedColumns(value: any) {
        this._selectedColumns = value;
    }

    @ViewChild('dt') table: Table;
    @ViewChild('rechercheVente') rechercheVente: ElementRef;
    @ViewChild('menuContextuelItemVente') menuContextuelItemVente: ContextMenu;
    
    constructor(
        sdb: DonneesBaseService,
        profilService: ProfilService,
        route: ActivatedRoute, 
        appMain: AppMainComponent, 
        location: Location, 
        private breadcrumbService: BreadcrumbService,
        private venteService: VenteService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        public dialogService: DialogService,
        public routerService: Router) {

        super(sdb, profilService, route, routerService, appMain);
            
        this.breadcrumbService.setItems([
            { label: 'Achats', routerLink: ['/achats'] },
            { label: 'Ventes' }
        ]);
    }

    ngOnInit() {
        this.verifierAccesPageAuMoisUnAcces(['ventes', 'ventes_consulter']);

        this.CompleterChargement();
        
        this.menuContextuel = [
            {
                label: 'Voir les détails',
                icon: 'pi pi-eye',
                visible: this.estAutorise('ventes_consulter'),
                command: () => this.OuvrirDetailVente(this.selectedVente.venteId)
            },
            {
                label: 'Modifier',
                icon: 'pi pi-pencil',
                visible: this.estAutorise('ventes_modifier'),
                command: () => this.ModifierVente(this.selectedVente)
            },
            {
                separator: true,
                visible: this.estAutorise('ventes_modifier')
            },
            {
                label: 'Supprimer',
                icon: 'pi pi-trash',
                visible: this.estAutorise('ventes_modifier'),
                command: () => this.SupprimerVente(this.selectedVente)
            }
        ];
    }

    ngAfterViewInit() {
        this.rechercheVente?.nativeElement.focus();
        this.chargerColonnesSelectionnees();
    }

    private selectedVente: VenteEntite;

    /**
     * Handles lazy loading of ventes data
     * @param event LazyLoadEvent containing pagination, filtering, and sorting parameters
     */
    onLazyLoad(event: LazyLoadEvent) {
        this.chargementVentesComplete = false;
        
        if (this.lastRequest) {
            this.lastRequest.unsubscribe();
        }

        this.lastRequest = this.venteService.ObtenirVentes(event).subscribe({
            next: (response) => {
                this.ventes = response.elements;
                this.totalRecords = response.maxCount;
                this.chargementVentesComplete = true;
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erreur',
                    detail: 'Erreur lors du chargement des ventes'
                });
                this.chargementVentesComplete = true;
            }
        });
    }

    /**
     * Opens the detail view for a specific vente
     * @param venteId The ID of the vente to view
     */
    OuvrirDetailVente(venteId: number) {
        this.routerService.navigate(['/vente', venteId]);
    }

    /**
     * Opens the detail view for a specific vente in a new tab
     * @param venteId The ID of the vente to view
     */
    OuvrirDetailVenteNouvelOnglet(venteId: number) {
        const url = this.routerService.serializeUrl(
            this.routerService.createUrlTree(['/vente', venteId])
        );
        window.open(url, '_blank');
    }

    /**
     * Navigates to the add new vente page
     */
    AjouterVente() {
        this.routerService.navigate(['/vente/0']);
    }

    /**
     * Opens the modification dialog for a vente
     * @param vente The vente to modify
     */
    ModifierVente(vente: VenteEntite) {
        this.routerService.navigate(['/vente', vente.venteId]);
    }

    /**
     * Handles the context menu for vente items
     * @param event Mouse event
     * @param vente The vente associated with the context menu
     */
    surMenuContextuelItem(event: any, vente: VenteEntite) {
        this.selectedVente = vente;
        this.menuContextuelItemVente.show(event);
    }

    /**
     * Confirms and deletes a vente
     * @param vente The vente to delete
     */
    SupprimerVente(vente: VenteEntite) {
        this.confirmationService.confirm({
            message: `Êtes-vous sûr de vouloir supprimer la vente ${vente.numero}?`,
            header: 'Confirmation de suppression',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.venteService.SupprimerVente(vente.venteId).then(response => {
                    if (response.estUnSucces) {
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Succès',
                            detail: 'Vente supprimée avec succès'
                        });
                        this.venteService.ClearCacheVentes();
                        this.table.reset();
                    } else {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Erreur',
                            detail: response.messages?.[0]?.description || 'Erreur lors de la suppression'
                        });
                    }
                }).catch(error => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erreur',
                        detail: 'Erreur lors de la suppression de la vente'
                    });
                });
            }
        });
    }

    /**
     * Clears all table filters
     * @param table The table instance
     */
    clearFilters(table: Table) {
        table.clear();
        this.globalFilter = '';
    }

    /**
     * Checks if a specific column is selected for display
     * @param field The field name to check
     * @returns boolean indicating if the column is selected
     */
    EstColonneSelectionnee(field: string): boolean {
        return this.selectedColumns.some(col => col.champ === field);
    }
}
